<script setup lang="ts">
	import { MzButton, MzDialog, MzIcon, MzTitle, useToast } from '@monizze/monizze-components';
	import { useI18n } from 'vue-i18n';
	import { ref } from 'vue';
	import { Api } from '@/utils/api.ts';
	import { PhPlug, PhTrash } from '@phosphor-icons/vue';

	type TokenCollectionType = {
		[key: string]: TokenType;
	};

	type TokenType = {
		client_id: number;
		name: string;
		scopes: string[];
	};

	const { t } = useI18n();
	const toast = useToast();
	const tokens = ref<TokenType[]>([]);
	const revokeTarget = ref<TokenType | null>(null);

	Api.get('/tokens').then((response) => {
		response.json().then((data: TokenCollectionType) => {
			tokens.value = Object.values(data);
		});
	});

	const confirmRevoke = async () => {
		if (!revokeTarget.value) return;
		const target = revokeTarget.value;
		revokeTarget.value = null;
		await Api.delete(`/token/${target.client_id}`);
		tokens.value = tokens.value.filter((token) => token.client_id !== target.client_id);
		toast.add({
			title: t('user.access.revoked.title'),
			description: t('user.access.revoked.description'),
			variant: 'success',
		});
	};
</script>

<template>
	<div class="flex flex-col gap-8 max-w-[800px] mx-auto w-full">
		<header class="flex flex-col gap-2">
			<mz-title type="h1" :text="t('user.access.title')" />
			<p>{{ t('user.access.subtitle') }}</p>
		</header>

		<section class="bg-bg-box-default border border-border-box-default rounded-md p-4 flex flex-col gap-6">
			<mz-title type="h4" :text="t('user.access.third_party')" />
			<p v-if="tokens.length === 0" class="text-text-placeholder">{{ t('user.access.empty') }}</p>
			<div v-else class="flex flex-col">
				<div
					v-for="token in tokens"
					:key="token.client_id"
					class="flex items-center gap-4 py-4 border-b border-border-default last:border-b-0"
				>
					<MzIcon :icon="PhPlug" size="sm" class="text-text-action shrink-0" />
					<div class="flex flex-col gap-1 flex-1 min-w-0">
						<p class="font-bold">{{ token.name }}</p>
						<p>{{ token.scopes.map((scope) => t(`auth.scopes.${scope}`)).join(', ') }}</p>
					</div>
					<mz-button variant="destructive" :icon="PhTrash" :aria-label="t('user.access.revoke')" @click="revokeTarget = token" />
				</div>
			</div>
		</section>

		<mz-dialog
			:open="revokeTarget !== null"
			:title="revokeTarget ? t('user.access.revoke_modal.title', { appName: revokeTarget.name }) : ''"
			size="medium"
			@update:open="(open) => !open && (revokeTarget = null)"
		>
			<p>{{ t('user.access.revoke_modal.description') }}</p>
			<template #footer>
				<mz-button variant="minimal" :label="t('general.cancel')" @click="revokeTarget = null" />
				<mz-button variant="destructive" :label="t('user.access.revoke_modal.confirm')" @click="confirmRevoke" />
			</template>
		</mz-dialog>
	</div>
</template>
